<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { ref, computed, watch } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { Users, UserCheck, UserX, Search, ChevronRight, Mail, Calendar, Crown, Sparkles, Star, Clock } from 'lucide-vue-next'

interface Subscription {
  id: number
  tier_name: string
  tier_slug: string
  amount: number
  status: string
  status_display: string
  is_live: boolean
  created_at: string
  cancelled_at: string | null
  selected_slot: {
    day?: string
    time?: string
    subject_name?: string
    level_name?: string
  } | null
}

interface Student {
  id: number
  name: string
  email: string
  avatar_url: string | null
  subscribed_at: string
  subscriptions: Subscription[]
  active_subscription: {
    tier_name: string
    tier_slug: string
  } | null
  total_spent: number
}

interface Stats {
  pending_requests: number
  active_students: number
  cancelled_subscriptions: number
}

interface Filters {
  status: string | null
  search: string | null
  tier: string | null
}

const props = defineProps<{
  students: Student[]
  stats: Stats
  filters: Filters
}>()

const searchQuery = ref(props.filters.search || '')
const statusFilter = ref(props.filters.status || '')
const tierFilter = ref(props.filters.tier || '')
const isLoading = ref(false)

const statusOptions = [
  { value: '', label: 'All Students' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'paused', label: 'Paused' },
]

const tierOptions = [
  { value: '', label: 'All Tiers' },
  { value: 'base', label: 'Base' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
]

function getTierIcon(tierSlug: string) {
  switch (tierSlug) {
    case 'gold':
      return Crown
    case 'silver':
      return Sparkles
    default:
      return Star
  }
}

function getTierColor(tierSlug: string): string {
  switch (tierSlug) {
    case 'gold':
      return 'text-yellow-600 bg-yellow-50'
    case 'silver':
      return 'text-gray-600 bg-gray-100'
    default:
      return 'text-blue-600 bg-blue-50'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-700 bg-green-50'
    case 'pending':
      return 'text-blue-700 bg-blue-50'
    case 'cancelled':
      return 'text-gray-700 bg-gray-100'
    case 'rejected':
      return 'text-red-700 bg-red-50'
    case 'expired':
      return 'text-gray-700 bg-gray-100'
    case 'paused':
      return 'text-amber-700 bg-amber-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function formatSlot(slot: Subscription['selected_slot']): string {
  if (!slot) return ''
  const parts = []
  if (slot.day) parts.push(slot.day)
  if (slot.time) parts.push(slot.time)
  return parts.join(' at ')
}

/**
 * The subscriptions worth putting on the row: the ones that still stand.
 * A student who cancelled and resubscribed should read by where they are now,
 * not by everything they have ever bought. Where nothing is live, the most
 * recent dead subscription is shown so the row still says something.
 */
function shownSubscriptions(student: Student): Subscription[] {
  const live = student.subscriptions.filter((sub) => sub.is_live)
  return live.length > 0 ? live : student.subscriptions.slice(0, 1)
}

function applyFilters() {
  isLoading.value = true
  router.get('/teacher/students', {
    status: statusFilter.value || undefined,
    search: searchQuery.value || undefined,
    tier: tierFilter.value || undefined,
  }, {
    preserveState: true,
    preserveScroll: true,
    onFinish: () => {
      isLoading.value = false
    },
  })
}

// Debounced search
let searchTimeout: number | null = null
watch(searchQuery, () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 300) as unknown as number
})

watch(statusFilter, () => {
  applyFilters()
})

watch(tierFilter, () => {
  applyFilters()
})
</script>

<template>
  <Head title="My Students" />

  <DashboardLayout>
    <div class="w-full max-w-6xl space-y-6">
      <div class="space-y-0.5">
        <h2 class="text-2xl font-bold tracking-tight">My Students</h2>
        <p class="text-gray-500">View and manage your subscribed students.</p>
      </div>

      <!-- Stats Grid -->
      <div class="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Pending</CardTitle>
            <Clock class="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold text-amber-600">{{ stats.pending_requests }}</div>
            <p class="text-xs text-gray-500">Awaiting your review</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Active</CardTitle>
            <UserCheck class="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold text-green-600">{{ stats.active_students }}</div>
            <p class="text-xs text-gray-500">Currently subscribed</p>
          </CardContent>
        </Card>
      </div>

      <!-- Filters -->
      <Card>
        <CardContent class="pt-6">
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- Search -->
            <div class="relative flex-1">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by name or email..."
                class="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Tier Filter -->
            <select
              v-model="tierFilter"
              class="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option v-for="option in tierOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <!-- Status Filter -->
            <select
              v-model="statusFilter"
              class="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </CardContent>
      </Card>

      <!-- Students List -->
      <Card>
        <CardHeader>
          <CardTitle>Students</CardTitle>
          <CardDescription>
            {{ students.length }} student{{ students.length !== 1 ? 's' : '' }} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <div v-else-if="students.length === 0" class="text-center py-12 text-gray-400">
            <Users class="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p class="mb-2 font-medium text-gray-600">No students found</p>
            <p class="text-sm">
              {{ filters.status || filters.search ? 'Try adjusting your filters.' : 'When students subscribe to your plans, they will appear here.' }}
            </p>
          </div>

          <div v-else class="divide-y divide-gray-100">
            <Link
              v-for="student in students"
              :key="student.id"
              :href="`/teacher/students/${student.id}`"
              class="block py-4 hover:bg-gray-50 -mx-6 px-6 transition-colors cursor-pointer"
            >
              <div class="flex items-start gap-4">
                <!-- Avatar -->
                <div class="shrink-0">
                  <img
                    v-if="student.avatar_url"
                    :src="student.avatar_url"
                    :alt="student.name"
                    class="w-12 h-12 rounded-full object-cover"
                  />
                  <div v-else class="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-semibold text-lg">
                    {{ student.name.charAt(0).toUpperCase() }}
                  </div>
                </div>

                <!-- Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="font-semibold text-gray-900 truncate">{{ student.name }}</h3>
                    <span
                      v-if="student.active_subscription"
                      :class="[getTierColor(student.active_subscription.tier_slug), 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium']"
                    >
                      <component :is="getTierIcon(student.active_subscription.tier_slug)" class="w-3 h-3" />
                      {{ student.active_subscription.tier_name }}
                    </span>
                    <span v-else class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-gray-500 bg-gray-100">
                      Inactive
                    </span>
                  </div>

                  <div class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-sm text-gray-500 mb-2">
                    <span class="flex items-center gap-1">
                      <Mail class="w-3.5 h-3.5" />
                      {{ student.email }}
                    </span>
                    <span class="flex items-center gap-1">
                      <Calendar class="w-3.5 h-3.5" />
                      Joined {{ student.subscribed_at }}
                    </span>
                  </div>

                  <!-- Current Subscription Details -->
                  <div v-if="shownSubscriptions(student).length > 0" class="flex flex-wrap gap-2">
                    <template v-for="sub in shownSubscriptions(student).slice(0, 2)" :key="sub.id">
                      <div class="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                        <span :class="[getStatusColor(sub.status), 'px-1.5 py-0.5 rounded text-xs font-medium']">
                          {{ sub.status_display }}
                        </span>
                        <span class="text-gray-600">{{ sub.tier_name }}</span>
                        <span v-if="sub.selected_slot && formatSlot(sub.selected_slot)" class="text-gray-400 flex items-center gap-1">
                          <Clock class="w-3 h-3" />
                          {{ formatSlot(sub.selected_slot) }}
                        </span>
                      </div>
                    </template>
                    <span v-if="shownSubscriptions(student).length > 2" class="text-xs text-gray-400 self-center">
                      +{{ shownSubscriptions(student).length - 2 }} more
                    </span>
                  </div>
                </div>

                <!-- Arrow -->
                <div class="shrink-0 flex items-center self-center">
                  <ChevronRight class="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  </DashboardLayout>
</template>
